import type { EarlyStageVerificationStatus } from "../../api/earlyStageMembershipApi";
import { isEarlyStageMembership } from "../../utils/membershipTier";

export type RegistrationUserSnapshot = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  city?: string | null;
  personalPositionCompany?: string | null;
  businessName?: string | null;
  membershipName?: string | null;
  isTermConditions?: number | null;
  hasPaid?: number | null;
  earlyStagePriceTier?: string | null;
};

/** Pending under-23 review but user chose to proceed at £10/month now. */
export function earlyStagePendingProceedingAtStandard(
  status: EarlyStageVerificationStatus | null,
  user?: Pick<RegistrationUserSnapshot, "earlyStagePriceTier"> | null,
): boolean {
  const s = (status?.status ?? "").trim();
  if (s !== "Pending" && s !== "UnderReview") return false;
  const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
  return tier === "Premium10";
}

/**
 * True only when Personal Info step fields are complete (same required set as
 * personalInfoSchema). Auth signup/login name+email alone must not count as done.
 */
export function hasRegistrationPersonalInfoSaved(
  user: RegistrationUserSnapshot,
): boolean {
  return Boolean(
    user.firstName?.trim() &&
      user.lastName?.trim() &&
      user.email?.trim() &&
      user.phoneNumber?.trim() &&
      user.city?.trim() &&
      user.personalPositionCompany?.trim() &&
      user.businessName?.trim(),
  );
}

export function isEarlyStageVerificationInProgress(
  status: EarlyStageVerificationStatus | null,
): boolean {
  const s = (status?.status ?? "").trim();
  return (
    s === "Pending" ||
    s === "UnderReview" ||
    s === "Approved" ||
    s === "Rejected" ||
    s === "Expired"
  );
}

/** User chose or was placed on the standard £10/month Early Stage path (not under-23 YP review). */
export function isEarlyStageStandardTenPoundPathCommitted(
  status: EarlyStageVerificationStatus | null,
  user?: Pick<RegistrationUserSnapshot, "earlyStagePriceTier"> | null,
): boolean {
  const verificationStatus = (status?.status ?? "").trim();
  const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
  const ageTier = (status?.ageTier ?? "").trim();

  if (earlyStagePendingProceedingAtStandard(status, user)) return true;

  if (verificationStatus === "Rejected" && tier === "Premium10") return true;

  // Direct £10 path: NotSubmitted (or legacy Approved without admin) + Premium10 + 23+.
  if (
    tier === "Premium10" &&
    (ageTier === "TwentyThreePlus" || ageTier === "23Plus")
  ) {
    return (
      !verificationStatus ||
      verificationStatus === "NotSubmitted" ||
      verificationStatus === "Approved"
    );
  }

  return false;
}

/** Approved under-23 who still must pick £2 or £10 before terms/checkout. */
export function needsEarlyStagePricePlanSelection(
  status: EarlyStageVerificationStatus | null,
): boolean {
  if ((status?.status ?? "").trim() !== "Approved") return false;
  const priceTier = status?.priceTier?.trim() ?? "";
  if (priceTier === "Standard2" || priceTier === "Premium10") return false;
  return Boolean(status?.canSelectPriceTier);
}

/**
 * Early Stage only (7 steps). Caller must pass DB user row only — not
 * localStorage / form drafts (those prefill the UI but must not skip steps).
 * £2 YP Pending/UnderReview resumes at age step without requiring Personal Info.
 * 0 personal → 1 membership → 2 age verification → 3 payment plan (under 23) →
 * 4 terms → 5 checkout → 6 confirmation
 */
export function resolveEarlyStageRegistrationStep(
  user: RegistrationUserSnapshot,
  status: EarlyStageVerificationStatus | null,
): number {
  if (user.hasPaid === 1) return 6;

  const verificationStatus = (status?.status ?? "").trim();

  if (verificationStatus === "Pending" || verificationStatus === "UnderReview") {
    if (earlyStagePendingProceedingAtStandard(status, user)) {
      if (user.isTermConditions !== 1) return 4;
      return 5;
    }
    return 2;
  }
  if (verificationStatus === "Rejected") {
    const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
    if (tier === "Premium10") {
      if (user.isTermConditions !== 1) return 4;
      return 5;
    }
    return 2;
  }
  if (verificationStatus === "Approved") {
    const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
    const ageTier = (status?.ageTier ?? "").trim();
    if (needsEarlyStagePricePlanSelection(status)) {
      return 2;
    }
    if (
      tier === "Premium10" &&
      (ageTier === "TwentyThreePlus" || ageTier === "23Plus")
    ) {
      if (user.isTermConditions === 1) return 5;
      return 4;
    }
    if (user.isTermConditions === 1) return 5;
    return 2;
  }

  // Direct £10 Early Stage (no admin Approved): resume terms/checkout from local/DB flags.
  if (
    isEarlyStageMembership(user.membershipName) &&
    (verificationStatus === "NotSubmitted" || !verificationStatus)
  ) {
    const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
    const ageTier = (status?.ageTier ?? "").trim();
    if (
      tier === "Premium10" &&
      (ageTier === "TwentyThreePlus" || ageTier === "23Plus")
    ) {
      if (user.isTermConditions === 1) return 5;
      return 4;
    }
  }

  if (!hasRegistrationPersonalInfoSaved(user)) return 0;
  if (!isEarlyStageMembership(user.membershipName)) return 1;

  return 1;
}

/** Standard tiers (5 steps). Uses DB user row only. */
export function resolveStandardRegistrationStep(
  user: RegistrationUserSnapshot,
): number {
  if (user.hasPaid === 1) return 4;
  if (!hasRegistrationPersonalInfoSaved(user)) return 0;
  if (!user.membershipName?.trim()) return 1;
  if (user.isTermConditions !== 1) return 2;
  return 3;
}
